import type { QueryKey } from '@tanstack/react-query';
import type { FieldValues } from 'react-hook-form';

import { useFormScreen, type FormScreenModel, type UseFormScreenOptions } from '@/shared/form';

import { FLOW_PARAM, flowLeaves, type FlowDefinition } from './define-flow';
import { useFlow, useFlowDraft, type FlowDraftOptions, type FlowModel } from './use-flow';

export type UseFlowScreenOptions<V extends FieldValues, R, B, Res, K extends QueryKey = QueryKey> = Omit<UseFormScreenOptions<V, R, B, Res, K>, 'id' | 'leaves'> & {
  /** Editar um registro existente em passos; ausente cria. */
  readonly id?: string | undefined;
  /**
   * Rascunho no `sessionStorage` (F5 volta ao passo com os valores): só os caminhos de `include`, por
   * tenant e usuário; limpo ao salvar e no logout (`clearFlowDrafts`). Desligado no somente leitura.
   */
  readonly draft?: FlowDraftOptions<V> | undefined;
};

export interface FlowScreenModel<V extends FieldValues, R = unknown> {
  readonly screen: FormScreenModel<V, R>;
  readonly flow: FlowModel<V>;
}

/**
 * `useFormScreen` + `useFlow` (receita da categoria `fluxo`): trocar de passo não aciona o guarda de
 * saída, e salvar limpa o rascunho. Passe o resultado inteiro ao `FlowScreen`.
 */
export function useFlowScreen<V extends FieldValues, R = unknown, B = unknown, Res = unknown, K extends QueryKey = QueryKey>(
  definition: FlowDefinition<V>,
  options: UseFlowScreenOptions<V, R, B, Res, K>,
): FlowScreenModel<V, R> {
  const { draft: draftOption, onSaved, id, ...rest } = options;
  const draft = useFlowDraft(definition.id, draftOption);
  const screen = useFormScreen<V, R, B, Res, K>({
    ...rest,
    id,
    leaves: flowLeaves(definition.param ?? FLOW_PARAM),
    onSaved: (result, context) => {
      draft?.clear();
      return onSaved?.(result, context);
    },
  });
  const flow = useFlow(definition, screen.form, { draft, readOnly: screen.readOnly });
  return { screen, flow };
}
